import { ChevronRight } from "lucide-react";
import { type JSX, type ReactNode, useId, useState } from "react";
import { cx } from "../cx";

export interface DisclosureProps {
  /** Summary-row text; also the accessible name of the toggle. */
  label: string;
  /**
   * Right-aligned summary shown while collapsed (and kept while open) so
   * the section communicates its state without being expanded — for
   * example "14 rows" or a status pill.
   */
  meta?: ReactNode | undefined;
  defaultOpen?: boolean | undefined;
  /** Controlled open state; leave undefined for uncontrolled use. */
  open?: boolean | undefined;
  onToggle?: ((open: boolean) => void) | undefined;
  className?: string | undefined;
  children?: ReactNode | undefined;
}

/**
 * Progressive-disclosure section: a hairline summary row that reveals
 * secondary detail on demand. The default posture is collapsed — use it
 * for introspection, framing, and metadata that supports but does not
 * serve a surface's primary intent. Never put error/warning diagnostics
 * or fail-closed states behind one.
 */
export function Disclosure({
  label,
  meta,
  defaultOpen = false,
  open,
  onToggle,
  className,
  children,
}: DisclosureProps): JSX.Element {
  const regionId = useId();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isOpen = open ?? uncontrolledOpen;

  const toggle = (): void => {
    const next = !isOpen;
    if (open === undefined) {
      setUncontrolledOpen(next);
    }
    onToggle?.(next);
  };

  return (
    <section className={cx("tk-disclosure", isOpen && "tk-disclosure--open", className)}>
      <div className="tk-disclosure__summary">
        <button
          type="button"
          className="tk-disclosure__toggle"
          aria-expanded={isOpen}
          aria-controls={regionId}
          onClick={toggle}
        >
          <ChevronRight size={14} aria-hidden="true" className="tk-disclosure__chevron" />
          <span className="tk-disclosure__label">{label}</span>
        </button>
        {meta === undefined ? null : <span className="tk-disclosure__meta">{meta}</span>}
      </div>
      <div id={regionId} hidden={!isOpen}>
        {isOpen ? <div className="tk-disclosure__body">{children}</div> : null}
      </div>
    </section>
  );
}
